import * as React from "react";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { SectionHeader } from "../../SharedComponents";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { DrawdownSwitchLabelMessage, DrawdownSwitchValidationMessage } from "./Resource/Contents";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { ChooseAssetsToSwitchAssetsTable } from "./ChooseAssetsToSwitchFromTable";
import { PartLoading } from "../../../../../../../react-core/Html/PartLoading";
import {
    calculateChooseAssetsToSwtichFromTotalAssets,
    calculateChooseAssetsToSwitchFromTotalForDrawdown,
    getAssetsToSellTotalForDrawdown,
    transformNewItem,
} from "./Helper/DrawdownDetailHelper";
import { SearchTabs } from "../../../../../../Shared/IST/Components/Shared/SearchTabs";
import { SwitchToAllocationTable } from "./SwitchToAllocationTable";
import { ChooseAssetsToSellAssets, ChooseAssetsToSwitchAssets } from "./Store/DrawdownCalculationStoreStatic";
import { PropositionInvestmentConfig } from "../../../../../../../Wrap/Models/Domains/Account/PropositionInvestmentConfig";
import { ServiceProposition } from "../../../../../../../Wrap/Constants/Enums/ServiceProposition";
import { SelectedInvestmentStore } from "../../../../../../Shared/IST/Store/Variable";
import { ActionType } from "../../../../../../../react-core/Flux/ActionType";
import { ClientAccountStore } from "../../../../../Stroes/Variable";
import { InvestmentSelectionAction } from "../../../../../../Shared/IST/Action/InvestmentSelectionAction";
import { CompanyWizardAction } from "../../../../../../Admin/Company/CompanyWizard/Actions/CompanyWizardAction";
import { SwitchInstructionModel } from "../../../../../../../Wrap/Models/Domains/Switch/SwitchInstructionModel";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { RestrictionStore } from "../../Restrictions/Variable";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { InvestmentAllocation } from "../../../../../../../Wrap/Models/Domains/Investment/InvestmentAllocation";
import { Instrument } from "../../../../../../../Wrap/Models/Domains/Investment/Instrument";
import { InvestmentsAction } from "../../../PortfolioDashboard/Components/Investment/Action/InvestmentAction";
import { TreasuredAssetsInstructionModel } from "../../../../../../../Wrap/Models/Domains/TreasuredAssets/TreasuredAssetsInstructionModel";
import { ProductType } from "../../../../../../../Wrap/Constants/Enums/Domain/ProductType";

interface ISwitchInvestmentsProps extends IProcessComponentProps {
    assets: ChooseAssetsToSellAssets[];
    assetsTable: ChooseAssetsToSwitchAssets[];
    disableOffplatform?: boolean;
}

interface ISwitchInvestmentsState {
    switchTotalValue?: number;
    switchMaxValue?: number;
    allocationAssetsTable: ChooseAssetsToSwitchAssets[];
    allowDecimals: boolean;
    isLoading: boolean;
    investmenPermission: PropositionInvestmentConfig[];
    companyServicePropostion: ServiceProposition[];
}

export class SwitchInvestments extends ProcessComponent<ISwitchInvestmentsProps, ISwitchInvestmentsState> {
    constructor(props: any) {
        super(props);
        this.state = {
            allowDecimals: false,
            isLoading: false,
            allocationAssetsTable: [],
            investmenPermission: [],
            companyServicePropostion: [],
            switchTotalValue: 0,
            switchMaxValue: 0
        };

        this.subscribeToStore(SelectedInvestmentStore, () => this.onStoreChange());
    }

    onStoreChange() {
        if (SelectedInvestmentStore.getActionType() == ActionType.Success) {
            const data = SelectedInvestmentStore.getData();
            if (!data) return;

            const allocationAssetsTable: ChooseAssetsToSwitchAssets[] = this.state.allocationAssetsTable.concat();

            const insAdd = data.filter((item) =>
                allocationAssetsTable.every((ele) => ele.ProductId !== item.Instrument.ProductId)
            );
            if (insAdd.length > 0) {
                const newItem = transformNewItem(insAdd);
                this.setState({ allocationAssetsTable: allocationAssetsTable.concat(newItem) });
            }
        }
    }

    private onChangeSwitchInstructionModel(allocation) {
        const switchInstructionModel: SwitchInstructionModel = this.props.data?.Contribution?.DrawdownModel
            ?.SwitchInstructionModel ?? {
            InstructionId: getQueryParameters()["id"],
            HierarchyId: this.props.data?.Contribution?.DrawdownModel?.HierarchyId,
        };

        if (allocation.BuyAllocations) {
            switchInstructionModel.BuyAllocations = allocation.BuyAllocations;

            switchInstructionModel.BuyAllocations.forEach((buyAllocation: InvestmentAllocation) => {
                buyAllocation.Value = {
                    Amount: this.state.switchTotalValue * (buyAllocation?.Percentage / 100 ?? 0),
                    Currency: CurrencyCode.GBP
                };
            });
        }

        if (allocation.SellAllocations) {
            switchInstructionModel.SellAllocations = allocation.SellAllocations;

            switchInstructionModel.SellAllocations.forEach((sellAllocation: InvestmentAllocation) => {
                const correspondingAsset = this.props.assetsTable.find(asset => asset.ProductId === sellAllocation.ProductId);
                const isSwitchAll = correspondingAsset?.SwitchAll;

                if (isSwitchAll && correspondingAsset?.MarketValue?.Amount) {
                    const pensionCommencementLumpSumAllocations = this.props.data?.Contribution?.DrawdownModel?.PensionCommencementLumpSumAllocations || [];
                    let paymentPercentage = pensionCommencementLumpSumAllocations.find((ele) => ele.ProductId == correspondingAsset.ProductId)?.Percentage ?? 0;
                    let sellTotal = getAssetsToSellTotalForDrawdown(this.props.data?.Contribution?.DrawdownModel);
                    let currentEstimatedValue = correspondingAsset.MarketValue.Amount - sellTotal * paymentPercentage / 100;

                    sellAllocation.Value = {
                        Amount: currentEstimatedValue,
                        Currency: CurrencyCode.GBP
                    };
                } else {
                    sellAllocation.Value = {
                        Amount: this.state.switchTotalValue * (sellAllocation?.Percentage / 100 ?? 0),
                        Currency: CurrencyCode.GBP
                    };
                }
            });
        }

        this.props.onChange({ SwitchInstructionModel: switchInstructionModel });
    }

    private onDeleteInvestment = (changes, productId) => {
        const _data: any[] = this.state.allocationAssetsTable.concat();
        const index = _data.findIndex((ele) => ele.ProductId == productId);
        if (index < 0) return _data;
        if (changes == -1) {
            _data.splice(index, 1);
        } else {
            const obj = _data[index];
            for (const key in changes) {
                obj[key] = changes[key];
            }
            _data[index] = obj;
        }
        this.setState({ allocationAssetsTable: _data });
    };

    private onDeleteInvestmentUpdateProps = (productId) => {
        const _data: any[] = (this.props.data.Contribution.DrawdownModel?.SwitchInstructionModel?.BuyAllocations ?? []).concat();
        const index = _data.findIndex((ele) => ele.ProductId == productId);
        if (index < 0) return _data;
        _data.splice(index, 1);

        this.onChangeSwitchInstructionModel({ BuyAllocations: _data });
    };

    private loadExistingInvestmentAllocationData(buyAllocations: InvestmentAllocation[]) {
        const hierarchyId = getQueryParameters()["hierarchyid"];
        InvestmentsAction.getAllocationAssetDetails(hierarchyId, buyAllocations, [],
            (result, buyAllocations, sellAllocations) => {
                this.setState({
                    allocationAssetsTable: this.transformToAssetsToSwitch(result, buyAllocations),
                });
            });
    }

    private loadExistingSwitchTotal(buyAllocations: InvestmentAllocation[]) {
        if (buyAllocations && buyAllocations.length > 0) {
            const existingSwitchTotal = Number(buyAllocations.reduce(
                (total, allocation) => total + (allocation.Value?.Amount || 0),
                0
            ).toFixed(2));

            if (existingSwitchTotal > 0) {
                this.setState({ switchTotalValue: existingSwitchTotal });
                return true;
            }
        }
        return false;
    }

    private transformToAssetsToSwitch(instruments: Instrument[], allocations: InvestmentAllocation[]): ChooseAssetsToSwitchAssets[] {
        if (instruments.length == 0) return [];
        let transformAssetsToSwitch: ChooseAssetsToSwitchAssets[] = [];
        allocations.forEach((x) => {
            var instrument = instruments.find(y => y.ProductId == x.ProductId);
            if (instrument) {
                let asset: ChooseAssetsToSwitchAssets = {
                    ProductId: x.ProductId,
                    ProductCode: instrument.ProductCode,
                    Assets: instrument.Name,
                    ProductType: instrument.Type,
                    SwitchAll: x.Percentage == 100
                };
                transformAssetsToSwitch.push(asset);
            }
        });
        return transformAssetsToSwitch;
    }

    private handleSwitchAllChange = (targetRow: ChooseAssetsToSwitchAssets, switchAll: boolean) => {
        const currentAllocations = this.props.data?.Contribution?.DrawdownModel?.SwitchInstructionModel?.SellAllocations || [];
        const updatedAllocations = [...currentAllocations];

        const updatedTableData = this.props.assetsTable.map(row =>
            row.ProductId === targetRow.ProductId ? { ...row, SwitchAll: switchAll } : row
        );

        targetRow.SwitchAll = switchAll;

        if (switchAll) {
            const switchAllAssets = updatedTableData.filter(row => row.SwitchAll);
            const pensionCommencementLumpSumAllocations = this.props.data?.Contribution?.DrawdownModel?.PensionCommencementLumpSumAllocations || [];
            const sellTotal = getAssetsToSellTotalForDrawdown(this.props.data?.Contribution?.DrawdownModel);

            const totalSwitchAllValue = Number(switchAllAssets.reduce((sum, asset) => {
                let paymentPercentage = pensionCommencementLumpSumAllocations.find((ele) => ele.ProductId == asset.ProductId)?.Percentage ?? 0;
                let currentEstimatedValue = (asset.MarketValue?.Amount || 0) - sellTotal * paymentPercentage / 100;
                return sum + currentEstimatedValue;
            }, 0).toFixed(2));

            if (totalSwitchAllValue > this.state.switchTotalValue) {
                this.setState({
                    switchTotalValue: totalSwitchAllValue,
                    allowDecimals: false
                }, () => {
                    this.recalculatePercentagesForSwitchAll(updatedTableData, updatedAllocations);
                });
            } else {
                this.recalculatePercentagesForSwitchAll(updatedTableData, updatedAllocations);
            }
        } else {
            const remainingSwitchAllAssets = updatedTableData.filter(row => row.SwitchAll);

            if (remainingSwitchAllAssets.length > 0) {
                const pensionCommencementLumpSumAllocations = this.props.data?.Contribution?.DrawdownModel?.PensionCommencementLumpSumAllocations || [];
                const sellTotal = getAssetsToSellTotalForDrawdown(this.props.data?.Contribution?.DrawdownModel);

                const newTotalSwitchAllValue = Number(remainingSwitchAllAssets.reduce((sum, asset) => {
                    let paymentPercentage = pensionCommencementLumpSumAllocations.find((ele) => ele.ProductId == asset.ProductId)?.Percentage ?? 0;
                    let currentEstimatedValue = (asset.MarketValue?.Amount || 0) - sellTotal * paymentPercentage / 100;
                    return sum + currentEstimatedValue;
                }, 0).toFixed(2));

                this.setState({
                    switchTotalValue: Math.max(newTotalSwitchAllValue, this.state.switchTotalValue),
                    allowDecimals: false
                }, () => {
                    this.recalculatePercentagesForSwitchAll(updatedTableData, updatedAllocations);
                });
            } else {
                this.onChangeSwitchInstructionModel({
                    BuyAllocations: this.props.data?.Contribution?.DrawdownModel?.SwitchInstructionModel?.BuyAllocations,
                    SellAllocations: updatedAllocations
                });
            }
        }
    }

    private recalculatePercentagesForSwitchAll = (tableData: ChooseAssetsToSwitchAssets[], allocations: InvestmentAllocation[]) => {
        const switchAllAssets = tableData.filter(row => row.SwitchAll);
        const currentTotalValue = this.state.switchTotalValue;

        const pensionCommencementLumpSumAllocations = this.props.data?.Contribution?.DrawdownModel?.PensionCommencementLumpSumAllocations || [];
        const sellTotal = getAssetsToSellTotalForDrawdown(this.props.data?.Contribution?.DrawdownModel);

        switchAllAssets.forEach(asset => {
            let paymentPercentage = pensionCommencementLumpSumAllocations.find((ele) => ele.ProductId == asset.ProductId)?.Percentage ?? 0;
            const assetValue = (asset.MarketValue?.Amount || 0) - sellTotal * paymentPercentage / 100;

            let percentage: number;

            if (assetValue >= currentTotalValue) {
                percentage = 100;
            } else {
                percentage = Math.round((assetValue / currentTotalValue) * 100 * 100) / 100; // Round to 2 decimal places
            }

            const existingIndex = allocations.findIndex(allocation => allocation.ProductId === asset.ProductId);
            const allocationData = {
                Name: asset.Assets,
                Percentage: percentage,
                ProductId: asset.ProductId,
                Type: asset.ProductType
            };

            if (existingIndex > -1) {
                allocations[existingIndex] = allocationData;
            } else {
                allocations.push(allocationData);
            }
        });

        this.onChangeSwitchInstructionModel({
            BuyAllocations: this.props.data?.Contribution?.DrawdownModel?.SwitchInstructionModel?.BuyAllocations,
            SellAllocations: allocations
        });
    };

    componentDidUpdate(prevProps: any): void {
        super.componentDidUpdate(prevProps);

        if (prevProps.data?.Contribution?.DrawdownModel?.CrystallisationDetails?.CrystallisationAmount?.Amount !=
            this.props.data?.Contribution?.DrawdownModel?.CrystallisationDetails?.CrystallisationAmount?.Amount ||
            prevProps.data?.Contribution?.DrawdownModel?.TaxFreeLumpSum?.Amount !=
            this.props.data?.Contribution?.DrawdownModel?.TaxFreeLumpSum?.Amount ||
            prevProps.data?.Contribution?.DrawdownModel?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Money?.Amount !=
            this.props.data?.Contribution?.DrawdownModel?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Money?.Amount ||
            prevProps.data?.Contribution?.DrawdownModel?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Percentage !=
            this.props.data?.Contribution?.DrawdownModel?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Percentage ||
            prevProps.data?.Contribution?.DrawdownModel?.InvestmentPathwayAnswers?.TakeUpInvestmentPathwayChoice !=
            this.props.data?.Contribution?.DrawdownModel?.InvestmentPathwayAnswers?.TakeUpInvestmentPathwayChoice
        ) {
            if (this.getPathwayText()) {
                const totalAssetMarketValueExcludeCash = this.props.assets.reduce((total, asset) => {
                    if (asset.ProductType != ProductType.Cash) {
                        return total + (asset.MarketValue?.Amount || 0);
                    }
                    return total;
                }, 0);
                
                const switchMaxVal = Number(calculateChooseAssetsToSwtichFromTotalAssets(this.props.data?.Contribution?.DrawdownModel, totalAssetMarketValueExcludeCash).toFixed(2));
    
                this.setState({ switchTotalValue: switchMaxVal, switchMaxValue: switchMaxVal });
            } else {
                const switchMaxVal = Number(calculateChooseAssetsToSwitchFromTotalForDrawdown(this.props.data?.Contribution?.DrawdownModel).toFixed(2));
    
                this.setState({ switchTotalValue: switchMaxVal, switchMaxValue: switchMaxVal });
            }
        }
        if (this.getPathwayText() && !this.props.data?.Contribution?.DrawdownModel?.SwitchInvestments) {
            this.props.onChange({ SwitchInvestments: true });
        }
        this.allocateInvestmentPathway();
    }

    componentDidMount() {
        super.componentDidMount();
        // this.getTreasuredAssets();
        const clientAccount = ClientAccountStore.getClientAccountInfo();
        if (clientAccount?.AccountServicePropositionId) {
            InvestmentSelectionAction.getInvestmentPermission(clientAccount.AccountServicePropositionId, (res) => {
                this.setState({ investmenPermission: res });
            });
        }
        if (clientAccount?.Company) {
            CompanyWizardAction.getViewCompany(clientAccount?.Company, (res) => {
                this.setState({ companyServicePropostion: res?.ServicePropositions });
            });
        }
        var buyAllocations = this.props.data?.Contribution?.DrawdownModel?.SwitchInstructionModel?.BuyAllocations;
        var hasExistingTotal = false;
        if (!isNullOrEmpty(buyAllocations)) {
            this.loadExistingInvestmentAllocationData(buyAllocations);
        }

        if (this.getPathwayText()) {
            const totalAssetMarketValueExcludeCash = this.props.assets.reduce((total, asset) => {
                if (asset.ProductType != ProductType.Cash) {
                    return total + (asset.MarketValue?.Amount || 0);
                }
                return total;
            }, 0);

            var switchMaxVal = Number(calculateChooseAssetsToSwtichFromTotalAssets(this.props.data?.Contribution?.DrawdownModel, totalAssetMarketValueExcludeCash).toFixed(2));
        } else {
            var switchMaxVal = Number(calculateChooseAssetsToSwitchFromTotalForDrawdown(this.props.data?.Contribution?.DrawdownModel).toFixed(2));
        }

        hasExistingTotal = this.loadExistingSwitchTotal(buyAllocations);

        if (!hasExistingTotal) {
            this.setState({ switchTotalValue: switchMaxVal });
        }

        this.setState({ switchMaxValue: switchMaxVal });
    }

    isValid() {
        if (this.props.data.Contribution?.DrawdownModel?.SwitchInvestments === false) return true;
        if (RestrictionStore.buyDisabled() || RestrictionStore.sellDisabled()) return false;
        let sellAllocations = this.props.data?.Contribution?.DrawdownModel?.SwitchInstructionModel?.SellAllocations;
        const hasNoInvestment = sellAllocations && sellAllocations.length == 0;
        if (hasNoInvestment) {
            this.props.showErrorMsgPopUp([DrawdownSwitchValidationMessage.noHoldings]);
        }
        return super.isValid() && !hasNoInvestment;
    }

    private onTreasuredAssetChange = (v: boolean, productId: number) => {
        const treasuredAssetsInstruction: TreasuredAssetsInstructionModel =
            this.props.data?.Contribution?.TreasuredAssetsInstruction ?? { ProductIds: [] };

        const productIndex: number = treasuredAssetsInstruction?.ProductIds?.findIndex(asset => asset === productId);

        if (productIndex !== -1) {
            if (!v) {
                treasuredAssetsInstruction.ProductIds.splice(productIndex, 1);
            }
        } else {
            if (v) {
                treasuredAssetsInstruction.ProductIds.push(productId);
            }
        }

        this.props.onChange({ TreasuredAssetsInstruction: treasuredAssetsInstruction });
    };

    private getPathwayText() {
        const investmentPathwayAnswers = this.props.data?.Contribution?.DrawdownModel?.InvestmentPathwayAnswers;
        if (!investmentPathwayAnswers?.TakeUpInvestmentPathwayChoice) return null;
        const productId = investmentPathwayAnswers?.LinkedProduct;
        return investmentPathwayAnswers?.InvestmentPathwaysSettingsForSettings?.find((setting) => setting.SelectedInstrumentId === productId).CurrentPathwayInvestmentText;
    }

    private allocateInvestmentPathway() {
        const investmentPathwayAnswers = this.props.data?.Contribution?.DrawdownModel?.InvestmentPathwayAnswers;
        if (!investmentPathwayAnswers?.TakeUpInvestmentPathwayChoice) return;

        const productId = investmentPathwayAnswers?.LinkedProduct;
        const buyAllocations = this.props.data?.Contribution?.DrawdownModel?.SwitchInstructionModel?.BuyAllocations ?? [];
        const allocationAssets = this.state.allocationAssetsTable ?? [];
        if (buyAllocations.length != 0 || allocationAssets.length != 1 || allocationAssets[0].ProductId != productId) return;

        buyAllocations.push({ Name: allocationAssets[0].Assets, Percentage: 100, ProductId: productId, Type: allocationAssets[0].ProductType })
        this.props.onChange({ SwitchInvestments: true });
        this.onChangeSwitchInstructionModel({ BuyAllocations: buyAllocations });
    }
    public render(): JSX.Element {
        const { onChange, data } = this.props;
        const clientAccount = ClientAccountStore.getClientAccountInfo();
        const selectedPathwayText = this.getPathwayText();

        return (
            <>
                {SectionHeader(DrawdownSwitchLabelMessage.switchInvestments)}
                <RadiosEditor
                    options={[true, false]}
                    label={DrawdownSwitchLabelMessage.switchInvestmentsQuestion}
                    getText={(v) => (v ? "Yes" : "No")}
                    onChange={(v) => {
                        onChange({ SwitchInvestments: v });

                        if (!v) {
                            this.onChangeSwitchInstructionModel({
                                BuyAllocations: [],
                                SellAllocations: []
                            });
                        }
                    }}
                    cssWrapper={"rg-inline mb-10"}
                    selected={data?.Contribution?.DrawdownModel?.SwitchInvestments ?? false}
                />
                <hr />
                {data?.Contribution?.DrawdownModel?.SwitchInvestments && (
                    <div className="ist-wrap">
                        <h2 className="mb-18">
                            <span>{DrawdownSwitchLabelMessage.switchFrom}</span>
                        </h2>
                        <MoneyTextBoxEditor
                            label={DrawdownSwitchLabelMessage.totalSwitchFrom}
                            cssWrapper="control-size-245"
                            allowDecimals={this.state.allowDecimals}
                            value={this.state.switchTotalValue ?? this.state.switchMaxValue}
                            currency={CurrencyCode.GBP}
                            onChange={(v) => {
                                this.setState({ switchTotalValue: v ? Number(v.toFixed(2)) : v, allowDecimals: false }, () => {
                                    if (this.props.data?.Contribution?.DrawdownModel?.SwitchInstructionModel) {
                                        const currentModel = this.props.data.Contribution.DrawdownModel.SwitchInstructionModel;

                                        this.onChangeSwitchInstructionModel({
                                            BuyAllocations: currentModel.BuyAllocations,
                                            SellAllocations: currentModel.SellAllocations
                                        });
                                    }
                                });
                            }}
                            onBlur={() => {
                                this.setState({ allowDecimals: true });
                            }}
                            maxNumberOfDecimalPlaces={{
                                errorMessage: DrawdownSwitchValidationMessage.allowDecimals,
                                value: 2,
                            }}
                            moreThan={{
                                value: 0,
                                errorMessage: DrawdownSwitchValidationMessage.valueMustLargerThan0,
                            }}
                            atMost={{
                                value: this.state.switchMaxValue,
                                errorMessage: DrawdownSwitchValidationMessage.valueMustNotLargerThan + this.state.switchMaxValue,
                            }}
                            required={DrawdownSwitchValidationMessage.enterRequired}
                        />{" "}
                        <span>(Max {MoneyDisplayHelper.FromatMoneyDisplay(this.state.switchMaxValue, CurrencyCode.GBP)})</span>
                        <PartLoading isLoading={this.state.isLoading}>
                            <ChooseAssetsToSwitchAssetsTable
                                data={data?.Contribution?.DrawdownModel?.SwitchInstructionModel?.SellAllocations || []}
                                tableData={this.props.assetsTable}
                                pensionCommencementLumpSumAllocations={
                                    data?.Contribution?.DrawdownModel?.PensionCommencementLumpSumAllocations
                                }
                                switchTotalValue={this.state.switchTotalValue ?? this.state.switchMaxValue}
                                sellTotal={getAssetsToSellTotalForDrawdown(data?.Contribution?.DrawdownModel)}
                                onRef={(ref) => this.appendChild(ref)}
                                showErrorMsgPopUp={this.props.showErrorMsgPopUp}
                                onChange={(v) => {
                                    this.onChangeSwitchInstructionModel({
                                        BuyAllocations: data?.Contribution?.DrawdownModel?.SwitchInstructionModel?.BuyAllocations,
                                        SellAllocations: v
                                    });
                                }}
                                onSwitchAllChange={this.handleSwitchAllChange}
                                disableOffplatform={this.props.disableOffplatform}
                            />
                        </PartLoading>
                        <br />
                        <h2 className="mb-18">
                            <span>{DrawdownSwitchLabelMessage.switchTo}</span>
                        </h2>
                        <MoneyTextBoxEditor
                            disabled
                            label={DrawdownSwitchLabelMessage.totalOrderValue}
                            cssWrapper="control-size-245"
                            allowDecimals
                            value={this.state.switchTotalValue ?? this.state.switchMaxValue}
                            currency={CurrencyCode.GBP}
                            onChange={() => { }}
                            maxNumberOfDecimalPlaces={{
                                errorMessage: DrawdownSwitchValidationMessage.allowDecimals,
                                value: 2,
                            }}
                            required={DrawdownSwitchValidationMessage.selectRequired}
                        />
                        <PartLoading isLoading={this.state.isLoading}>
                            <SearchTabs
                                hiercharyId={data.ExistingAccountHierarchyId ?? data.OwnerHierarchyId}
                                product={SubAccountType.Npr}
                                data={data?.Contribution?.DrawdownModel?.SwitchInstructionModel?.BuyAllocations}
                                isUnderTransfer={false}
                                investmentPermission={this.state.investmenPermission}
                                accountServiceProposition={clientAccount?.AccountServicePropositionId}
                                canBeBought={true}
                                canBeSold={false}
                                companyServiceProposition={this.state.companyServicePropostion}
                                selectedPathwayText={selectedPathwayText}
                            />
                        </PartLoading>
                        <h3 className="mb-0">Allocation</h3>
                        <SwitchToAllocationTable
                            data={data?.Contribution?.DrawdownModel?.SwitchInstructionModel?.BuyAllocations || []}
                            tableData={this.state.allocationAssetsTable}
                            switchMaxValue={this.state.switchTotalValue ?? this.state.switchMaxValue}
                            onRef={(ref) => this.appendChild(ref)}
                            showErrorMsgPopUp={this.props.showErrorMsgPopUp}
                            onChange={(v) => {
                                this.onChangeSwitchInstructionModel({ BuyAllocations: v });
                            }}
                            onTableDataDelete={(changes, productId) => {
                                this.onDeleteInvestment(changes, productId);
                                this.onDeleteInvestmentUpdateProps(productId);
                            }}
                            onTreasuredAssetChange={this.onTreasuredAssetChange}
                            ChecktreasuredAsset={this.props.data?.Contribution?.DrawdownModel?.TreasuredAssetsInstruction?.ProductIds ?? []}
                        />
                    </div>
                )}
            </>
        );
    }
}
